import React from "react";
import styled from "styled-components";
import { EVT_MOUSEOVER, KEY_UP_ARROW, KEY_DOWN_ARROW } from "../../../../lib";
import { i18n } from "../../../../services";
import TextField from "./TextField";
import LockIcon from "./LockIcon";
import UnLockIcon from "./UnLockIcon";
import ResetIcon from "./ResetIcon";
import { ID_EDITOR_CONTENTS } from "../../Contents/styles";
import { saveEditorContents } from "../../Contents/helpers";

const Container = styled.div<{
    enableDarkTheme?: boolean;
    top: number;
    right: number;
}>`
    pointer-events: none;
    z-index: 10;
    user-select: none;
    position: absolute;
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    justify-content: center;
    align-items: center;
    top: ${(p) => p.top + 5}px;
    right: ${(p) => p.right + 5}px;
    height: 33px;
    padding: 0 10px 0 0;
    color: ${(p) => (p.enableDarkTheme ? "#000" : "#fff")};
    background-color: ${(p) => (p.enableDarkTheme ? "#fff" : "#000")};
`;
const Label = styled.div<{ enableDarkTheme?: boolean }>`
    margin-left: 10px;
    font-family: urdu;
    font-weight: 500;
    color: ${(p) => (p.enableDarkTheme ? "#000" : "#fff")};
`;

type Props = {
    enableDarkTheme: boolean;
};
type State = {
    locked: boolean;
    show: boolean;
    width: number | null;
    height: number | null;
};
export class ResizeImage extends React.Component<Props, State> {
    private top: number = 0;
    private right: number = 0;
    private fieldWidth: React.RefObject<HTMLInputElement>;
    private elmContents: HTMLDivElement | null = null;

    constructor(props: Props) {
        super(props);
        this.state = {
            locked: true,
            show: false,
            width: null,
            height: null,
        };

        this.fieldWidth = React.createRef<HTMLInputElement>();
    }

    componentDidMount() {
        this.elmContents = document.getElementById(
            ID_EDITOR_CONTENTS
        ) as HTMLDivElement;
        this.elmContents.addEventListener(EVT_MOUSEOVER, this.onMouseOver);
    }

    componentWillUnmount() {
        if (!this.elmContents) {
            return;
        }

        this.elmContents.removeEventListener(EVT_MOUSEOVER, this.onMouseOver);
        this.elmContents = null;
    }

    private target: HTMLImageElement | null = null;
    private size: {
        width: number | null;
        height: number | null;
    } = {
        width: null,
        height: null,
    };

    onMouseOver = (evt: MouseEvent) => {
        const t = evt.target;
        if (!t) {
            return;
        }

        const img = t as HTMLImageElement;
        if (img.nodeName.toLowerCase() !== "img") {
            this.target = null;
            this.size = {
                width: null,
                height: null,
            };
            this.setState({ show: false });
            return;
        }

        this.target = img;
        const r = img.getBoundingClientRect();
        this.top = r.top;
        this.right = window.innerWidth - r.right;

        this.size.width = Math.round(r.width);
        this.size.height = Math.round(r.height);

        this.setState(
            {
                show: true,
                width: Math.abs(r.right - r.left),
                height: Math.abs(r.top - r.bottom),
            },
            () => {
                // this.fieldWidth && this.fieldWidth.focus()
            }
        );
    };

    onKeyUpWidth = (evt: React.KeyboardEvent<HTMLInputElement>) => {
        const { width, height, locked } = this.state;
        if (!width) {
            return;
        }

        if (evt.key === KEY_UP_ARROW) {
            const percentChange = (width + 1) / width;
            this.setState(
                {
                    width: width + 1,
                    height: locked ? height! * percentChange : height,
                },
                () => this.refreshTarget()
            );
        } else if (evt.key === KEY_DOWN_ARROW) {
            const percentChange = (width - 1) / width;
            this.setState(
                {
                    width: width - 1,
                    height: locked ? height! * percentChange : height,
                },
                () => this.refreshTarget()
            );
        }
    };

    onKeyUpHeight = (evt: React.KeyboardEvent<HTMLInputElement>) => {
        const { width, height, locked } = this.state;

        if (!height) {
            return;
        }

        if (evt.key === KEY_UP_ARROW) {
            const percentChange = (height + 1) / height;
            this.setState(
                {
                    height: height + 1,
                    width: locked ? width! * percentChange : width,
                },
                this.refreshTarget
            );
        } else if (evt.key === KEY_DOWN_ARROW) {
            const percentChange = (height - 1) / height;
            this.setState(
                {
                    height: height - 1,
                    width: locked ? width! * percentChange : width,
                },
                this.refreshTarget
            );
        }
    };

    onChangeWidth = (evt: React.ChangeEvent<HTMLInputElement>) => {
        if (!this.size.width || !this.size.height) {
            return;
        }

        const nextWidth = Number.parseInt(evt.target.value);

        if (!Number.isNaN(nextWidth) && nextWidth > 0) {
            const percentChange = nextWidth / this.size.width;
            const nextHeight = percentChange * this.size.height;
            this.setState(
                {
                    width: nextWidth,
                    height: nextHeight,
                },
                this.refreshTarget
            );
        }
    };

    onChangeHeight = (evt: React.ChangeEvent<HTMLInputElement>) => {
        if (!this.size.width || !this.size.height) {
            return;
        }

        const nextHeight = Number.parseInt(evt.target.value);

        if (!Number.isNaN(nextHeight) && nextHeight > 0) {
            const percentChange = nextHeight / this.size.height;
            const nextWidth = percentChange * this.size.width;
            this.setState(
                {
                    width: nextWidth,
                    height: nextHeight,
                },
                this.refreshTarget
            );
        }
    };

    refreshTarget = () => {
        if (this.target) {
            const { width, height } = this.state;
            if (!width || !height) {
                return;
            }

            if (width >= 20 && height >= 20) {
                this.target.width = width;
                this.target.height = height;
                saveEditorContents();
            }
        }
    };

    onToggleLock = () => this.setState({ locked: !this.state.locked });

    onReset = () => {
        if (!this.target) {
            return;
        }

        this.target.removeAttribute("width");
        this.target.removeAttribute("height");
        saveEditorContents();
    };

    render() {
        const { show, locked } = this.state;
        if (!show) {
            return null;
        }

        const { enableDarkTheme } = this.props;
        const fill = enableDarkTheme ? "#000" : "#fff";

        return (
            <Container top={this.top} right={this.right}>
                <Label>{i18n.get("resize.width")}</Label>
                <TextField
                    width="75"
                    attachRef={this.fieldWidth}
                    textAlign="center"
                    value={this.state.width!}
                    onKeyUp={this.onKeyUpWidth}
                    onChange={this.onChangeWidth}
                    enableDarkTheme={enableDarkTheme}
                    placeholder={i18n.get("resize.width")}
                />

                {locked ? (
                    <LockIcon fill={fill} onClick={this.onToggleLock} />
                ) : (
                    <UnLockIcon fill={fill} onClick={this.onToggleLock} />
                )}

                <Label>{i18n.get("resize.height")}</Label>
                <TextField
                    width="75"
                    textAlign="center"
                    onKeyUp={this.onKeyUpHeight}
                    onChange={this.onChangeHeight}
                    value={this.state.height!}
                    enableDarkTheme={enableDarkTheme}
                    placeholder={i18n.get("resize.height")}
                />

                <ResetIcon fill={fill} onClick={this.onReset} />
            </Container>
        );
    }
}
